import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if running in standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone;
    setIsStandalone(standalone);

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Listen for the beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Don't show if already dismissed
      const dismissed = localStorage.getItem('installPromptDismissed');
      if (!dismissed) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS prompt after a delay if not installed
    if (ios && !standalone) {
      const dismissed = localStorage.getItem('installPromptDismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Desktop install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
    // iOS - just keep the prompt open showing instructions
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  // Don't show if already installed or dismissed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed left-4 right-4 z-50 bottom-[calc(5.25rem+env(safe-area-inset-bottom))] md:bottom-4 md:left-auto md:right-4 md:w-96">
      <div className="bg-[#18181b] border-2 border-[#EAB308] rounded-2xl p-4 shadow-2xl">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-[#a1a1aa] hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="bg-[#EAB308]/20 p-2 rounded-lg">
            <Download className="w-6 h-6 text-[#EAB308]" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-heading font-bold mb-1">
              Install Winner Giveaway
            </h3>
            
            {isIOS ? (
              <div className="space-y-2">
                <p className="text-sm text-[#a1a1aa]">
                  Install this app on your iPhone:
                </p>
                <ol className="text-xs text-[#a1a1aa] space-y-1 list-decimal list-inside">
                  <li>Tap the <strong>Share</strong> button below</li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>Add</strong> in the top right</li>
                </ol>
              </div>
            ) : (
              <p className="text-sm text-[#a1a1aa] mb-3">
                Add to your home screen for quick access and a better experience!
              </p>
            )}
            
            {!isIOS && deferredPrompt && (
              <Button
                onClick={handleInstallClick}
                className="w-full bg-[#EAB308] hover:bg-[#CA8A04] text-black font-semibold mt-3"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Install Now
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
